import { Action, ActionType, FrameStencil, MovementType } from "../../../types";
import {
  DEFAULT_PASS_DURATION,
  getActionStencils,
  getBallActions,
  getBallMarkers,
  getPlayerAndPossessionActions,
  getPlayerMarkers,
  getPossessionActions,
} from "../../../utils";

export interface GetActionMinTimeProps {
  action: Action;
  frameStencils: FrameStencil[];
  startTime: number;
}

export const getActionMinTime = ({ action, frameStencils, startTime }: GetActionMinTimeProps): number => {
  const { actionType } = action;
  const actions = getActionStencils(frameStencils);
  const playerAndPossessionActions = getPlayerAndPossessionActions(actions);
  const possessionActions = getPossessionActions(actions);
  const ballActions = getBallActions(actions);

  const players = getPlayerMarkers(frameStencils);
  const balls = getBallMarkers(frameStencils);

  switch (actionType) {
    /** A player action is constrained by
     * 1. the end time of the previous player action
     * 2. the end time of a pass that is being received */
    case ActionType.PLAYER: {
      // get all actions that have this action as their nextPlayerAction
      const prevActions = playerAndPossessionActions.filter((a) => a.nextPlayerAction === action.id);
      return Math.max(...prevActions.map(({ endTime }) => endTime), startTime);
    }
    case ActionType.POSSESSION: {
      const prevActions = playerAndPossessionActions.filter((a) => a.nextPlayerAction === action.id);
      const playerOnAction = players.find((p) => p.movements.some((m) => m.id === action.id));
      // get passes directly to this player, excluding passes to an action end
      const passToPlayer = ballActions.find((b) => b.nextPlayer === playerOnAction?.id && !b.playerActionEnd);
      const passToPlayerEndTime = passToPlayer?.endTime || startTime;

      // get passes that have a playerActionEnd on the prevAction of this possession
      const passToPlayerActionEnd = ballActions.find((b) =>
        prevActions.some((prevAction) => prevAction.id === b.playerActionEnd)
      );
      const passToPlayerActionEndTime = passToPlayerActionEnd?.endTime || startTime;

      return Math.max(
        ...prevActions.map(({ endTime }) => endTime),
        passToPlayerEndTime,
        passToPlayerActionEndTime,
        startTime
      );
    }
    case ActionType.BALL: {
      // get possessions that have this pass as its nextBallAction
      const prevActions = possessionActions.filter((a) => a.nextBallAction === action.id);

      // get pass's playerActionEnd action and subtract default pass duration from that action's endTime
      const passPlayerActionEnd = playerAndPossessionActions.find((a) => a.id === action.playerActionEnd);
      const passPlayerActionEndTime = (passPlayerActionEnd?.endTime || DEFAULT_PASS_DURATION) - DEFAULT_PASS_DURATION;

      // find passes preceding this pass
      // find the pass action on the ball's movements, then get the previous possession movement startTime
      const prevPossessionMovement = balls.flatMap((b) => {
        const passIndex = b.movements.findIndex((m) => m.id === action.id);
        const prevPossession = b.movements[passIndex - 1];
        return prevPossession && prevPossession.movementType === MovementType.POSSESSION ? [prevPossession] : [];
      })?.[0];
      const prevPossessionMovementStartTime = prevPossessionMovement?.startTime || startTime;

      return Math.max(
        ...prevActions.map(({ endTime }) => endTime),
        passPlayerActionEndTime,
        prevPossessionMovementStartTime,
        startTime
      );
    }
    case ActionType.HANDOFF:
      return startTime;
  }
};
